import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { styled } from 'styled-components';

import { Button } from '@fabric-app/ui/components/Button';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { SearchIcon, CloseIcon } from '@fabric-app/icons/icons';
import { P, SectionTitle } from '@fabric-app/ui/components/Typography';

import { DashboardContentEmptyContainer } from '@fabric-app/business/dashboard/components/DashboardContent';
import { Flex } from '@fabric-app/ui/components/Flex';
import { useMutationRecentSearchRemove } from '@fabric-app/business/search/mutations/useMutationRecentSearchRemove';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { useQueryRecentSearch } from '@fabric-app/business/search/queries/useQueryRecentSearch';
import { useMutationRecentSearchRemoveAll } from '@fabric-app/business/search/mutations/useMutationRecentSearchRemoveAll';
import { EmptyState } from '@fabric-app/ui/components/EmptyState/EmptyState';
import { useThemedRive } from '@/src/ui/rive/useThemedRive';

const CloseButton = styled(Button).attrs({
  variant: 'transparent',
  shape: 'square',
  size: 'xs',
})``;

const SearchItemContainer = styled(motion.div).attrs({
  gap: 'elementsContainer',
  justifyContent: 'space-between',
  alignItems: 'center',
  as: Flex,
})`
  padding-left: 0.5rem;
  height: 1.75rem;
  border-radius: ${cssVar['radius-input']};
  cursor: pointer;
  overflow: hidden;

  ${CloseButton} {
    opacity: 0;
  }

  transition: 0.15s background;
  ${mediaHover} {
    &:hover {
      background: ${cssVar['color-bg-tertiary']};

      ${CloseButton} {
        opacity: 1;
      }
    }
  }
`;

type OnSelectSearchItem = (val: string) => void;
const RecentSearchItem: React.FC<{ searchKeyword: string; onSelect: OnSelectSearchItem }> = ({
  searchKeyword,
  onSelect,
}) => {
  const mutationRemoveRecentSearch = useMutationRecentSearchRemove(searchKeyword);
  return (
    <SearchItemContainer
      {...framerAnimationFade}
      onClick={() => {
        onSelect(searchKeyword);
      }}
    >
      <Flex gap="element" alignItems="center" overflow="hidden">
        <SearchIcon width="14px" style={{ color: cssVar['color-text-tertiary'] }} />
        <P color="secondary" ellipsis>
          {searchKeyword}
        </P>
      </Flex>
      <Flex gap="element" alignItems="center" flexShrink={0}>
        <CloseButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            mutationRemoveRecentSearch.mutate(searchKeyword);
          }}
        >
          <CloseIcon height="12px" width="12px" />
        </CloseButton>
      </Flex>
    </SearchItemContainer>
  );
};

const RADAR_STATE_MACHINE = 'State Machine 1';
const RivCanvas = styled.canvas`
  height: 480px;
  width: 480px;
  max-width: 70vh;
  max-height: 70vh;
  opacity: 0;
  transition-delay: 200ms;
  transition: 1s opacity;
  margin-bottom: -50px;
`;

const EmptyStateRiv = () => {
  const { setCanvasRef, isRiveLoaded } = useThemedRive({
    src: '/rive/radar.riv',
    stateMachine: RADAR_STATE_MACHINE,
  });
  return (
    <Flex direction="column" alignItems="center" justifyContent="center">
      <RivCanvas ref={setCanvasRef} style={{ opacity: isRiveLoaded ? 0.5 : 0 }} />
      <EmptyState.TextContainer
        style={
          isRiveLoaded
            ? { opacity: 1, transform: 'translateY(0)' }
            : {
                opacity: 0,
                transform: 'translateY(30px)',
              }
        }
      >
        <EmptyState.Title>Search with AI</EmptyState.Title>
        <EmptyState.Description>
          Find anything by describing it,
          <br /> uploading an image or choosing a colour
        </EmptyState.Description>
      </EmptyState.TextContainer>
    </Flex>
  );
};

export const RecentSearch: React.FC<{ onSelect: OnSelectSearchItem }> = ({ onSelect }) => {
  const queryRecentSearch = useQueryRecentSearch({
    select: (data) => {
      // get unique items
      const dataSet = new Set(data);
      return Array.from(dataSet).slice(0, 10);
    },
  });

  const mutationRemoveAllRecentSearch = useMutationRecentSearchRemoveAll();

  if (!queryRecentSearch.data || queryRecentSearch.data.length === 0) {
    return (
      <DashboardContentEmptyContainer style={{ width: '100%', overflow: 'hidden' }}>
        <EmptyStateRiv />
      </DashboardContentEmptyContainer>
    );
  }

  return (
    <Flex
      direction="column"
      gap="element"
      as={motion.div}
      {...framerAnimationFade}
      style={{ width: '100%' }}
    >
      <Flex justifyContent="space-between">
        <SectionTitle>Recent searches</SectionTitle>
        <Button
          size="xs"
          variant="transparent"
          border="default"
          onClick={() => {
            mutationRemoveAllRecentSearch.mutate();
          }}
        >
          Clear
        </Button>
      </Flex>
      <AnimatePresence>
        {queryRecentSearch.data.map((searchKeyword) => (
          <RecentSearchItem key={searchKeyword} searchKeyword={searchKeyword} onSelect={onSelect} />
        ))}
      </AnimatePresence>
    </Flex>
  );
};
